import React, { useEffect } from 'react';
import { Marker, Circle, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';

// Create a custom user location icon using the app icon
const createUserLocationIcon = () => {
  return L.icon({
    iconUrl: '/user-location-marker.png',
    iconSize: [48, 48], // Slightly larger than POI markers
    iconAnchor: [24, 24], // Center the icon
    popupAnchor: [0, -24],
    className: 'user-location-marker'
  });
};

interface UserLocationMarkerProps {
  latitude: number;
  longitude: number;
  accuracy?: number;
  centerOnUser?: boolean;
}

const UserLocationMarker: React.FC<UserLocationMarkerProps> = ({
  latitude,
  longitude,
  accuracy,
  centerOnUser = false
}) => {
  const map = useMap();

  // Center map on user location when requested
  useEffect(() => {
    if (centerOnUser) {
      map.setView([latitude, longitude], map.getZoom());
    }
  }, [latitude, longitude, centerOnUser, map]);

  const userIcon = createUserLocationIcon();

  return (
    <>
      {/* Accuracy circle - show behind the marker */}
      {accuracy && accuracy < 1000 && (
        <Circle
          center={[latitude, longitude]}
          radius={accuracy}
          pathOptions={{
            color: '#7fb069',
            fillColor: '#7fb069',
            fillOpacity: 0.1,
            weight: 2,
            dashArray: '8, 8'
          }}
        />
      )}

      {/* User location marker with app icon */}
      <Marker
        position={[latitude, longitude]}
        icon={userIcon}
        zIndexOffset={1000} // Ensure it appears above other markers
      >
        <Popup>
          <div className="text-center min-w-[200px]">
            <div className="flex items-center justify-center mb-2">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-2">
                <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 5.5V6C15 7.1 13.1 8 12 8S9 7.1 9 6V5.5L3 7V9L9 10.5V16H7V22H17V16H15V10.5L21 9Z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Your Location</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 mb-2">
              📍 {latitude.toFixed(6)}, {longitude.toFixed(6)}
            </p>
            {accuracy && (
              <div className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 rounded px-2 py-1">
                <span className="inline-flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Accuracy: ±{Math.round(accuracy)}m
                </span>
              </div>
            )}
          </div>
        </Popup>
      </Marker>
    </>
  );
};

export default UserLocationMarker;
